// Maps Shopify's SubscriptionBillingAttempt error_code (and the older, lowercase pre-2026-04 codes)
// to the ~20 categories Loop groups them into: https://help.loopwork.co/en/articles/12712734-payment-errors
// CARD_DECLINED, DO_NOT_HONOR, BILLING_AGREEMENT_CANCELLED, CARD_RETRY_DECLINED and SHOPIFY_DECLINED
// are folded into GENERIC_ERROR below to avoid over-fragmenting near-identical decline messaging.
const ERROR_CODE_TO_CATEGORY = {
  CALL_ISSUER: "GENERIC_ERROR",
  CARD_DECLINED: "GENERIC_ERROR",
  PAYMENT_METHOD_DECLINED: "GENERIC_ERROR",
  card_declined: "GENERIC_ERROR",

  DO_NOT_HONOR: "GENERIC_ERROR",

  EXPIRED_CARD: "CARD_EXPIRED",
  EXPIRED_PAYMENT_METHOD: "CARD_EXPIRED",
  INVALID_EXPIRY_DATE: "CARD_EXPIRED",
  card_expired: "CARD_EXPIRED",

  CARD_NUMBER_INCORRECT: "INCORRECT_CARD_NUMBER",
  INCORRECT_NUMBER: "INCORRECT_CARD_NUMBER",
  INVALID_NUMBER: "INCORRECT_CARD_NUMBER",
  card_invalid: "INCORRECT_CARD_NUMBER",

  INSUFFICIENT_FUNDS: "INSUFFICIENT_FUNDS",
  insufficient_funds: "INSUFFICIENT_FUNDS",

  INVALID_PAYMENT_METHOD: "INVALID_PAYMENT_METHOD",

  INVALID_PURCHASE_TYPE: "CARD_TYPE_NOT_SUPPORTED",
  PAYMENT_METHOD_UNSUPPORTED: "CARD_TYPE_NOT_SUPPORTED",
  PURCHASE_TYPE_NOT_SUPPORTED: "CARD_TYPE_NOT_SUPPORTED",
  card_not_supported: "CARD_TYPE_NOT_SUPPORTED",

  PAYMENT_METHOD_NOT_FOUND: "PAYMENT_METHOD_REVOKED",
  PAYMENT_METHOD_NOT_SPECIFIED: "PAYMENT_METHOD_REVOKED",
  payment_revoked: "PAYMENT_METHOD_REVOKED",

  FRAUD_SUSPECTED: "FRAUD_SUSPECTED",
  MERCHANT_RULE: "FRAUD_SUSPECTED",
  CONTRACT_UNDER_REVIEW: "FRAUD_SUSPECTED",

  INCORRECT_ADDRESS: "INVALID_ADDRESS",
  INCORRECT_ZIP: "INVALID_ADDRESS",
  INVALID_BILLING_ADDRESS: "INVALID_ADDRESS",
  INVALID_SHIPPING_ADDRESS: "INVALID_ADDRESS",

  AUTHENTICATION_ERROR: "AUTHENTICATION_FAILED",
  AUTHENTICATION_FAILED: "AUTHENTICATION_FAILED",
  AUTHENTICATION_REQUIRED: "AUTHENTICATION_FAILED",
  CONFIRMATION_REJECTED: "AUTHENTICATION_FAILED",
  "3DS_CHALLENGE_EXPIRED_LOOP": "AUTHENTICATION_FAILED",

  BUYER_CANCELED_PAYMENT_METHOD: "PAYPAL_AGREEMENT_CANCELLED",

  INVALID_CUSTOMER_BILLING_AGREEMENT: "PAYPAL_AGREEMENT_INVALID",

  PAYPAL_ERROR_GENERAL: "PAYPAL_GENERAL_ERROR",
  paypal_error: "PAYPAL_GENERAL_ERROR",

  CANCELLED_PAYMENT: "GENERIC_ERROR",
  agreement_cancelled: "GENERIC_ERROR",

  CUSTOMER_INVALID: "INVALID_CUSTOMER",
  CUSTOMER_NOT_FOUND: "INVALID_CUSTOMER",

  EXPIRED_BUYER_ACTION: "GENERIC_ERROR",
  GENERIC_ERROR: "GENERIC_ERROR",
  OFF_SESSION_REJECTED: "GENERIC_ERROR",
  UNEXPECTED_ERROR: "GENERIC_ERROR",
  not_confirmed_payments: "GENERIC_ERROR",

  FREE_GIFT_CARD_NOT_ALLOWED: "INVALID_GIFT_CARD_AMOUNT",

  INSUFFICIENT_INVENTORY: "INSUFFICIENT_INVENTORY",

  INVENTORY_ALLOCATIONS_NOT_FOUND: "NO_INVENTORY_LOCATION",
  inventory_allocations_not_found: "NO_INVENTORY_LOCATION",

  INVALID_CURRENCY: "INVALID_CURRENCY",

  INVOICE_ALREADY_PAID: "INVOICE_ALREADY_PAID",

  MERCHANT_ACCOUNT_ERROR: "MERCHANT_ACCOUNT_ERROR",

  NON_TEST_ORDER_LIMIT_REACHED: "GATEWAY_TEST_MODE_ERROR",
  PAYMENT_METHOD_INCOMPATIBLE_WITH_GATEWAY_CONFIG: "GATEWAY_TEST_MODE_ERROR",
  TEST_MODE: "GATEWAY_TEST_MODE_ERROR",

  PAYMENT_PROVIDER_ERROR: "PAYMENT_PROVIDER_ERROR",
  PROCESSING_ERROR: "PAYMENT_PROVIDER_ERROR",
  gateway_issue: "PAYMENT_PROVIDER_ERROR",

  PAYMENT_PROVIDER_IS_NOT_ENABLED: "PAYMENT_PROVIDER_NOT_ENABLED",
  shoppay_not_enabled: "PAYMENT_PROVIDER_NOT_ENABLED",

  RETRY_DECLINED: "GENERIC_ERROR",

  TRANSACTION_LIMIT_EXCEEDED: "TRANSACTION_LIMIT_EXCEEDED",

  TRANSIENT_ERROR: "TRANSIENT_ERROR",

  AMOUNT_TOO_LARGE: "AMOUNT_OUT_OF_RANGE",
  AMOUNT_TOO_SMALL: "AMOUNT_OUT_OF_RANGE",

  SHOPIFY_DECLINED: "GENERIC_ERROR",
  SHOPIFY_NO_CONFIRMATION: "GENERIC_ERROR",
};

class LoopErrorBanner extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const template = document.createElement("template");
    template.innerHTML = LoopErrorBanner.template;

    const content = template.content.cloneNode(true);
    this.appendChild(content);

    // Error-specific copy (falls back to the generic message above if the code isn't mapped)
    const errorCode = window.loopProps?.subscription?.lastBillingAttempt?.error_code;
    const category = errorCode && ERROR_CODE_TO_CATEGORY[errorCode];
    const categoryMessages =
      category &&
      window.LoopI18n.getTranslations()?.errors?.paymentError?.byCategory?.[
        category
      ];
    if (categoryMessages) {
      const titleEl = this.querySelector(".title");
      const descEl = this.querySelector(".desc");
      if (titleEl) titleEl.textContent = categoryMessages.title;
      if (descEl) descEl.textContent = categoryMessages.description;
    }

    const updateCardBtn = this.querySelector(".error-banner-cta");
    updateCardBtn.addEventListener("click", function () {
      document
        .querySelector("#loop-cp-payment-banners .loop-banner-footer .loop-btn")
        .click();
    });

    const retryBtn = this.querySelector(".error-banner-retry");
    retryBtn.addEventListener("click", async function () {
      const subscriptionId = window.loopProps?.subscription?.id;
      if (!subscriptionId) return;

      retryBtn.disabled = true;
      retryBtn.textContent = "...";

      try {
        await window.LoopService.Subscription.placeOrder({
          subscriptionId,
          country: window.Shopify?.countryCode || "PT",
        });
      } catch (error) {
        console.error("[ErrorBanner] Retry payment failed:", error);
      } finally {
        window.location.reload();
      }
    });

    // Price info
    const sub = window.loopProps?.subscription;
    if (sub) {
      let finalPrice = 0;
      let foundPrice = false;

      const jsonPrice = sub.totalLineItemDiscountedPrice || sub.totalLineItemPrice;
      if (jsonPrice && parseFloat(jsonPrice) > 0) {
        finalPrice = parseFloat(jsonPrice);
        foundPrice = true;
      } else if (sub.lineItems && sub.lineItems.length > 0) {
        finalPrice = sub.lineItems.reduce((acc, item) => {
          const p = item.discountedPrice || item.price || 0;
          return acc + parseFloat(p) * (item.quantity || 1);
        }, 0);
        if (finalPrice > 0) foundPrice = true;
      }

      if (foundPrice && finalPrice > 0) {
        const priceInfoEl = document.createElement("div");
        priceInfoEl.className = "flex gap-2 price-info";

        const priceTextEl = document.createElement("div");
        priceTextEl.className = "price-text bolder loop-h3 mb-0";
        priceTextEl.textContent = `${finalPrice.toFixed(2).replace(".", ",")}€`;

        const deliveryEl = document.createElement("div");
        deliveryEl.className = "text-sm loop-text-muted";
        const deliveryPrice = sub.deliveryPrice || 0;
        if (deliveryPrice > 0) {
          const fmt = deliveryPrice.toFixed(2).replace(".", ",");
          deliveryEl.textContent =
            window.t("shipping.shippingCost", fmt) || `+ ${fmt}€`;
        } else {
          deliveryEl.textContent =
            window.t("shipping.freeShipping") || "Free shipping";
        }

        priceInfoEl.appendChild(priceTextEl);
        priceInfoEl.appendChild(deliveryEl);

        const headerRow = this.querySelector(".error-banner-header");
        if (headerRow) {
          headerRow.appendChild(priceInfoEl);
        }
      }
    }
  }

  static template = html`
    <div>
      <div class="error-banner-header">
      <div class="info-error-icon">
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clip-path="url(#clip0_2632_3465)">
            <path
              d="M12 22C6.49 22 2 17.51 2 12C2 6.49 6.49 2 12 2C17.51 2 22 6.49 22 12C22 17.51 17.51 22 12 22ZM12 20C16.41 20 20 16.41 20 12C20 7.59 16.41 4 12 4C7.59 4 4 7.59 4 12C4 16.41 7.59 20 12 20ZM11.8701 14C11.5501 14 11.2895 13.7497 11.2695 13.4297L11.0303 8.62988C11.0103 8.28996 11.29 8.00007 11.6299 8H12.3701C12.71 8.00007 12.9897 8.28997 12.9697 8.62988L12.7305 13.4297C12.7105 13.7497 12.4499 14 12.1299 14H11.8701ZM12 17C11.45 17 11 16.55 11 16C11 15.45 11.45 15 12 15C12.55 15 13 15.45 13 16C13 16.55 12.55 17 12 17Z"
              fill="#1F1E1C"
            />
          </g>
          <defs>
            <clipPath id="clip0_2632_3465">
              <rect
                width="24"
                height="24"
                fill="white"
                transform="matrix(-1 0 0 -1 24 24)"
              />
            </clipPath>
          </defs>
        </svg>
      </div>
      </div>
      <div>
        <span class="bold title">
          ${window.t("errors.paymentError.title")}
        </span>
        <span class="desc">
          ${window.t("errors.paymentError.description")}
        </span>
      </div>
      <div class="error-banner-actions">
        <button class="error-banner-cta">
          ${window.t("errors.paymentError.cta")}
        </button>
        <button class="error-banner-retry">
          ${window.t("errors.paymentError.retry")}
        </button>
      </div>
    </div>
  `;
}

if (!customElements.get("loop-error-banner")) {
  customElements.define("loop-error-banner", LoopErrorBanner);
}
